import { Badge, Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui';
import { AdminMessageStatusEnum, MessageAudienceEnum, MessageAudienceLabelEnum } from '@/enums';
import type { IMessageDetailModalProps } from '@/interfaces';
import { useRequestCancelAdminMessage } from '@/hooks/httpRequest';
import { confirm } from '@/components/custom/react-confirm/Confirm.tsx';
import { notify } from '@/functions';
import { RotateCcw } from 'lucide-react';
import { useState } from 'react';
import dayjs from 'dayjs';
import { GENDER_LABELS, RELIGION_LABELS, countryName } from '../constants';

const STATUS_CLASS: Record<AdminMessageStatusEnum, string> = {
  [AdminMessageStatusEnum.COMPLETED]: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  [AdminMessageStatusEnum.PENDING]: 'bg-amber-100 text-amber-700 border-amber-200',
  [AdminMessageStatusEnum.CANCELLED]: 'bg-gray-100 text-gray-600 border-gray-200',
  [AdminMessageStatusEnum.ERROR]: 'bg-red-100 text-red-700 border-red-200'
};

const STATUS_LABEL: Record<AdminMessageStatusEnum, string> = {
  [AdminMessageStatusEnum.COMPLETED]: 'Envoyé',
  [AdminMessageStatusEnum.ERROR]: 'Erreur',
  [AdminMessageStatusEnum.PENDING]: 'En cours',
  [AdminMessageStatusEnum.CANCELLED]: 'Annulé'
};

const audienceLabel = (recipients: string[]): string => {
  if (recipients.length === 1) {
    if (recipients[0] === MessageAudienceEnum.ALL) return MessageAudienceLabelEnum.ALL;
    if (recipients[0] === MessageAudienceEnum.ALL_IOS) return MessageAudienceLabelEnum.ALL_IOS;
    if (recipients[0] === MessageAudienceEnum.ALL_ANDROID) return MessageAudienceLabelEnum.ALL_ANDROID;
  }
  return `${recipients.length} utilisateur${recipients.length > 1 ? 's' : ''} sélectionné${recipients.length > 1 ? 's' : ''}`;
};

const MessageDetailModal = ({ open, message, onClose, onResend }: IMessageDetailModalProps) => {
  const [cancelling, setCancelling] = useState(false);
  const { cancelMessage } = useRequestCancelAdminMessage({
    onSuccess: () => {
      notify('Message annulé.');
      onClose();
    }
  });

  if (!message) return null;

  const canCancel = message.status === AdminMessageStatusEnum.PENDING;

  const handleCancel = async () => {
    const yes = await confirm(
      'Etes-vous sur de vouloir annuler cet envoi ? Les messages déjà envoyés resteront visibles.'
    );
    if (!yes) return;

    try {
      setCancelling(true);
      await cancelMessage(message._id);
    } catch {
      notify("Erreur lors de l'annulation.", 'error');
    } finally {
      setCancelling(false);
    }
  };

  const isBroadcast = message.recipients.length === 1 &&
    Object.values(MessageAudienceEnum).includes(message.recipients[0] as MessageAudienceEnum) &&
    message.recipients[0] !== MessageAudienceEnum.SPECIFIC;

  const hasGenderFilter = isBroadcast && (message.genderFilter?.length ?? 0) > 0;
  const hasReligionFilter = isBroadcast && (message.religionFilter?.length ?? 0) > 0;
  const hasCountryFilter = isBroadcast && (message.countryFilter?.length ?? 0) > 0;
  const hasAgeFilter = isBroadcast && (message.minAge != null || message.maxAge != null);
  const hasExcludedUsers = isBroadcast && (message.excludedRecipientDetails?.length ?? 0) > 0;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className='w-[calc(100%-2rem)] sm:max-w-[600px] rounded-2xl duration-0 animate-none' showCloseIcon={false}>
        <DialogHeader>
          <div className='flex items-center justify-between gap-3'>
            <DialogTitle className='text-base'>Détail du message</DialogTitle>
            <Badge className={STATUS_CLASS[message.status]}>
              {STATUS_LABEL[message.status]}
            </Badge>
          </div>
          <p className='text-xs text-muted-foreground pt-1'>
            {dayjs(message.createdAt).format('DD/MM/YYYY à HH:mm')}
          </p>
        </DialogHeader>

        <div className='space-y-4 py-1 text-sm min-w-0 overflow-hidden'>
          {/* Messages */}
          <div className='space-y-2'>
            <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>Message FR</p>
            <p className='rounded-xl bg-muted/40 px-3 py-2 text-sm leading-relaxed wrap-break-word'>{message.messageFR}</p>
          </div>
          <div className='space-y-2'>
            <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>Message EN</p>
            <p className='rounded-xl bg-muted/40 px-3 py-2 text-sm leading-relaxed wrap-break-word'>{message.messageEN}</p>
          </div>

          {/* Audience & filters */}
          <div className='rounded-xl border border-border bg-muted/20 px-3 py-2 space-y-1.5 text-xs'>
            <div className='flex justify-between gap-4'>
              <span className='text-muted-foreground shrink-0'>Audience</span>
              <span className='font-medium text-foreground min-w-0 truncate text-right'>{audienceLabel(message.recipients)}</span>
            </div>
            {hasGenderFilter && (
              <div className='flex justify-between'>
                <span className='text-muted-foreground'>Genre</span>
                <span className='font-medium text-foreground'>
                  {message.genderFilter!.map(g => GENDER_LABELS[g] ?? g).join(' + ')}
                </span>
              </div>
            )}
            {hasReligionFilter && (
              <div className='flex justify-between'>
                <span className='text-muted-foreground'>Religion</span>
                <span className='font-medium text-foreground'>
                  {message.religionFilter!.map(r => RELIGION_LABELS[r] ?? r).join(', ')}
                </span>
              </div>
            )}
            {hasCountryFilter && (
              <div className='flex justify-between'>
                <span className='text-muted-foreground'>Pays</span>
                <span className='font-medium text-foreground'>{message.countryFilter!.map(countryName).join(', ')}</span>
              </div>
            )}
            {hasAgeFilter && (
              <div className='flex justify-between'>
                <span className='text-muted-foreground'>Âge</span>
                <span className='font-medium text-foreground'>
                  {message.minAge != null && message.maxAge != null
                    ? `${message.minAge}–${message.maxAge} ans`
                    : message.minAge != null
                      ? `${message.minAge} ans et plus`
                      : `${message.maxAge} ans et moins`}
                </span>
              </div>
            )}
            {hasExcludedUsers && (
              <div className='flex justify-between'>
                <span className='text-muted-foreground'>Exclusions</span>
                <span className='font-medium text-foreground'>
                  {message.excludedRecipientDetails!.length === 1
                    ? message.excludedRecipientDetails![0].name
                    : `${message.excludedRecipientDetails!.length} utilisateurs`}
                </span>
              </div>
            )}
          </div>

          {/* CTA */}
          {message.cta && (
            <div className='space-y-2'>
              <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>Action (CTA)</p>
              <div className='rounded-xl border border-border bg-muted/20 px-3 py-2 space-y-1.5 text-xs'>
                <div className='flex justify-between gap-4'>
                  <span className='text-muted-foreground shrink-0'>Texte FR</span>
                  <span className='font-medium text-foreground truncate text-right'>{message.cta.text?.fr}</span>
                </div>
                <div className='flex justify-between gap-4'>
                  <span className='text-muted-foreground shrink-0'>Texte EN</span>
                  <span className='font-medium text-foreground truncate text-right'>{message.cta.text?.en}</span>
                </div>
                <div className='flex justify-between gap-4'>
                  <span className='text-muted-foreground shrink-0'>Navigation</span>
                  <span className='font-medium text-foreground truncate text-right font-mono text-xs'>{message.cta.navigation?.appPath}</span>
                </div>
              </div>
            </div>
          )}

          {/* Error summary */}
          {message.status === AdminMessageStatusEnum.ERROR && message.errorSummary && (
            <div className='rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive space-y-1'>
              <p className='font-medium'>Résumé des erreurs</p>
              <p>{message.errorSummary.totalErrors} erreur(s) sur {message.errorSummary.totalProcessed} traité(s)</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose}>Fermer</Button>
          {canCancel && (
            <Button variant='destructive' disabled={cancelling} onClick={handleCancel}>
              {cancelling ? 'Annulation...' : 'Annuler'}
            </Button>
          )}
          {!canCancel && (
            <Button onClick={() => onResend(message)}>
              <RotateCcw className='mr-2 h-4 w-4' />
              Renvoyer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MessageDetailModal;
